import React, { useMemo, useState } from "react";
import { Pressable, StyleSheet, TextInput, View } from "react-native";
import {
  CaretDownIcon,
  CaretRightIcon,
  CheckIcon,
  ArrowCounterClockwiseIcon,
  PencilSimpleIcon,
  ShieldCheckIcon,
  UploadSimpleIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";

import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../../common/general/CustomText/CustomText";
import {
  RubricParseStatus,
  type ActiveRubricSetQuery,
  type RubricSetsBySchoolQuery,
} from "@axio/graphql/generated/graphql";
import RubricCriterionCard from "./RubricCriterionCard";

type RubricSetViewModel = NonNullable<ActiveRubricSetQuery["activeRubricSet"]["data"]>;
type RubricSectionViewModel = RubricSetViewModel["sections"][number];
type RubricCriterionViewModel = RubricSectionViewModel["criteria"][number];
type AvailableRubricViewModel = RubricSetsBySchoolQuery["rubricSetsBySchool"]["data"][number];

export interface RubricSectionPatch {
  id: string;
  title?: string;
  description?: string | null;
  resetToOriginal?: boolean;
}

export interface RubricCriterionPatch {
  id: string;
  title?: string;
  description?: string;
  sectionId?: string;
  isDeleted?: boolean;
  resetToOriginal?: boolean;
}

interface RubricReadyProps {
  rubric: RubricSetViewModel;
  availableRubrics: AvailableRubricViewModel[];
  loadingAvailableRubrics: boolean;
  savingRubric: boolean;
  activatingRubricId: string | null;
  onUpdateRubric: (input: { sections?: RubricSectionPatch[]; criteria?: RubricCriterionPatch[] }) => void;
  onActivateRubric: (rubricId: string) => void;
}

interface RubricReadyFooterProps {
  rubric: RubricSetViewModel;
  savingRubric: boolean;
  onUpdateRubric: (input: { sections?: RubricSectionPatch[]; criteria?: RubricCriterionPatch[] }) => void;
  onReplaceRubric: () => void;
}

const sortByDisplayOrder = <T extends { displayOrder: number }>(items: T[]) =>
  [...items].sort((left, right) => left.displayOrder - right.displayOrder);

const visibleCriteria = (criteria: RubricCriterionViewModel[]) =>
  sortByDisplayOrder(criteria).filter((criterion) => !criterion.isDeleted);

const sumMaxScore = (criteria: RubricCriterionViewModel[]) =>
  criteria.reduce((total, criterion) => total + (criterion.maxScore ?? 0), 0);

const formatSectionMeta = (criteriaCount: number, sectionMaxScore: number, percentage: number) => {
  const standardLabel = criteriaCount === 1 ? "standard" : "standards";
  const scoreText = sectionMaxScore > 0 ? ` - ${sectionMaxScore} pts` : "";
  return `${criteriaCount} ${standardLabel}${scoreText} (${percentage}%)`;
};

const formatCategoryBreakdownSummary = (totalMaxScore: number, totalCriteria: number) => {
  if (totalMaxScore > 0) {
    return `${totalMaxScore} points total`;
  }

  const standardLabel = totalCriteria === 1 ? "standard" : "standards";
  return `${totalCriteria} ${standardLabel}`;
};

function RubricReady({
  rubric,
  availableRubrics,
  loadingAvailableRubrics,
  savingRubric,
  activatingRubricId,
  onUpdateRubric,
  onActivateRubric,
}: RubricReadyProps) {
  const [expandedSectionIds, setExpandedSectionIds] = useState<Set<string>>(() => new Set());
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionDescription, setSectionDescription] = useState("");
  const sortedSections = useMemo(() => sortByDisplayOrder(rubric.sections), [rubric.sections]);
  const totalCriteria = useMemo(
    () => rubric.sections.reduce((count, section) => count + visibleCriteria(section.criteria).length, 0),
    [rubric.sections]
  );
  const totalMaxScore = useMemo(
    () => rubric.sections.reduce((total, section) => total + sumMaxScore(visibleCriteria(section.criteria)), 0),
    [rubric.sections]
  );
  const otherReadyRubrics = availableRubrics.filter(
    (availableRubric) =>
      availableRubric.id !== rubric.id &&
      !availableRubric.isArchived &&
      availableRubric.parseStatus === RubricParseStatus.Ready
  );

  const toggleSection = (sectionId: string) => {
    setExpandedSectionIds((previous) => {
      const next = new Set(previous);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const getEditCount = (section: RubricSectionViewModel) =>
    (section.isEdited ? 1 : 0) +
    section.criteria.filter((criterion) => criterion.isEdited || criterion.isDeleted).length;

  const handleStartSectionEdit = (section: RubricSectionViewModel) => {
    setEditingSectionId(section.id);
    setSectionTitle(section.title);
    setSectionDescription(section.description ?? "");
  };

  const handleSaveSectionEdit = () => {
    if (!editingSectionId) {
      return;
    }

    onUpdateRubric({
      sections: [
        {
          id: editingSectionId,
          title: sectionTitle,
          description: sectionDescription,
        },
      ],
    });
    setEditingSectionId(null);
  };

  return (
    <View style={styles.contentInner}>
      <View style={styles.activeRubricCard}>
        <View style={styles.activeRubricTitleRow}>
          <ShieldCheckIcon color="#3E68FE" size={18} weight="fill" />
          <View style={styles.activeRubricText}>
            <CustomText
              text={rubric.name}
              size="s"
              weight="bold"
              style={styles.primaryText}
              useTranslationText={false}
            />
            <CustomText
              text={rubric.detectedFrameworkType ?? rubric.sourceFile.fileName}
              size="xs"
              style={styles.secondaryText}
              useTranslationText={false}
            />
          </View>
          <View style={styles.readyBadge}>
            <CustomText
              text="Active"
              size="xs"
              weight="bold"
              style={styles.readyBadgeText}
              useTranslationText={false}
            />
          </View>
        </View>
      </View>

      {rubric.parseWarnings.length > 0 ? (
        <View style={styles.warningCallout}>
          <WarningCircleIcon color="#B54708" size={18} weight="bold" />
          <View style={styles.calloutText}>
            <CustomText
              text="Parse warnings"
              size="xs"
              weight="bold"
              style={styles.warningText}
              useTranslationText={false}
            />
            {rubric.parseWarnings.map((warning, warningIndex) => (
              <CustomText
                key={`${warningIndex}-${warning}`}
                text={warning}
                size="xs"
                style={styles.warningText}
                useTranslationText={false}
              />
            ))}
          </View>
        </View>
      ) : null}

      <CustomSpacing type="vertical" size="m" />

      <CustomText
        text={`Category Breakdown - ${formatCategoryBreakdownSummary(totalMaxScore, totalCriteria)}`}
        size="xs"
        weight="bold"
        style={styles.sectionEyebrow}
        useTranslationText={false}
      />
      <CustomSpacing type="vertical" size="s" />

      <View style={styles.sectionList}>
        {sortedSections.map((section) => {
          const isExpanded = expandedSectionIds.has(section.id);
          const sortedCriteria = visibleCriteria(section.criteria);
          const sectionMaxScore = sumMaxScore(sortedCriteria);
          const percentageBasis = totalMaxScore > 0 ? totalMaxScore : totalCriteria;
          const sectionValue = totalMaxScore > 0 ? sectionMaxScore : sortedCriteria.length;
          const sectionPercentage = percentageBasis > 0 ? Math.round((sectionValue / percentageBasis) * 100) : 0;
          const editCount = getEditCount(section);
          const isEditingSection = editingSectionId === section.id;

          return (
            <View key={section.id} style={styles.sectionCard}>
              {isEditingSection ? (
                <View style={styles.sectionEditPanel}>
                  <CustomText
                    text="Category Name"
                    size="xs"
                    weight="bold"
                    style={styles.inputLabel}
                    useTranslationText={false}
                  />
                  <TextInput
                    accessibilityLabel="Category name"
                    value={sectionTitle}
                    onChangeText={setSectionTitle}
                    style={styles.sectionTitleInput}
                  />
                  <CustomText
                    text="Description"
                    size="xs"
                    weight="bold"
                    style={styles.inputLabel}
                    useTranslationText={false}
                  />
                  <TextInput
                    accessibilityLabel="Category description"
                    value={sectionDescription}
                    onChangeText={setSectionDescription}
                    multiline
                    style={styles.sectionDescriptionInput}
                  />
                  <View style={styles.editActions}>
                    <CustomButton
                      text="Save"
                      size="s"
                      styleType="primary"
                      disabled={savingRubric}
                      useTranslationText={false}
                      onPress={handleSaveSectionEdit}
                      leftIcon={<CheckIcon color="#FFFFFF" size={14} weight="bold" />}
                    />
                    <CustomButton
                      text="Cancel"
                      size="s"
                      styleType="light"
                      disabled={savingRubric}
                      useTranslationText={false}
                      onPress={() => setEditingSectionId(null)}
                    />
                  </View>
                </View>
              ) : (
                <Pressable
                  role="button"
                  accessibilityLabel={`${isExpanded ? "Collapse" : "Expand"} ${section.title}`}
                  onPress={() => toggleSection(section.id)}
                  style={[styles.sectionHeader, isExpanded ? styles.sectionHeaderExpanded : null]}>
                  <View style={styles.sectionCaret}>
                    {isExpanded ? (
                      <CaretDownIcon color="#667085" size={16} weight="bold" />
                    ) : (
                      <CaretRightIcon color="#667085" size={16} weight="bold" />
                    )}
                  </View>
                  <View style={styles.sectionHeaderText}>
                    <View style={styles.sectionTitleRow}>
                      <CustomText
                        text={section.title}
                        size="s"
                        weight="medium"
                        style={styles.sectionTitle}
                        useTranslationText={false}
                      />
                      {editCount > 0 ? (
                        <View style={styles.editedBadge}>
                          <CustomText
                            text={isExpanded ? "Edited" : `${editCount} edited`}
                            size="xs"
                            weight="bold"
                            style={styles.editedBadgeText}
                            useTranslationText={false}
                          />
                        </View>
                      ) : null}
                      <CustomText
                        text={formatSectionMeta(sortedCriteria.length, sectionMaxScore, sectionPercentage)}
                        size="xs"
                        style={styles.sectionMetaText}
                        useTranslationText={false}
                      />
                    </View>
                    {section.description ? (
                      <CustomText
                        text={section.description}
                        size="xs"
                        style={styles.secondaryText}
                        useTranslationText={false}
                        numberOfLines={1}
                      />
                    ) : null}
                    <View style={styles.sectionProgressTrack}>
                      <View style={[styles.sectionProgressFill, { width: `${sectionPercentage}%` }]} />
                    </View>
                  </View>
                  {isExpanded ? (
                    <Pressable
                      role="button"
                      accessibilityLabel={`Edit ${section.title}`}
                      onPress={(event) => {
                        event.stopPropagation();
                        handleStartSectionEdit(section);
                      }}
                      style={styles.sectionEditButton}>
                      <PencilSimpleIcon color="#667085" size={15} weight="bold" />
                    </Pressable>
                  ) : null}
                </Pressable>
              )}

              {isExpanded && !isEditingSection ? (
                <View style={styles.criteriaList}>
                  {sortedCriteria.length > 0 ? (
                    sortedCriteria.map((criterion) => (
                      <RubricCriterionCard
                        key={criterion.id}
                        criterion={criterion}
                        sectionId={section.id}
                        sections={sortedSections}
                        isSaving={savingRubric}
                        onSave={(criterionId, title, description) =>
                          onUpdateRubric({ criteria: [{ id: criterionId, title, description }] })
                        }
                        onMove={(criterionId, sectionId) =>
                          onUpdateRubric({ criteria: [{ id: criterionId, sectionId }] })
                        }
                        onDelete={(criterionId) => onUpdateRubric({ criteria: [{ id: criterionId, isDeleted: true }] })}
                      />
                    ))
                  ) : (
                    <CustomText
                      text="No criteria in this category"
                      size="xs"
                      style={styles.secondaryText}
                      useTranslationText={false}
                    />
                  )}
                </View>
              ) : null}
            </View>
          );
        })}
      </View>

      <View style={styles.otherRubricsSection}>
        <CustomText
          text="Other Uploaded Rubrics"
          size="xs"
          weight="bold"
          style={styles.sectionEyebrow}
          useTranslationText={false}
        />
        {loadingAvailableRubrics ? (
          <CustomText
            text="Loading uploaded rubrics..."
            size="xs"
            style={styles.secondaryText}
            useTranslationText={false}
          />
        ) : otherReadyRubrics.length > 0 ? (
          <View style={styles.otherRubricsList}>
            {otherReadyRubrics.map((availableRubric) => (
              <View key={availableRubric.id} style={styles.otherRubricCard}>
                <View style={styles.activeRubricText}>
                  <CustomText
                    text={availableRubric.name}
                    size="xs"
                    weight="medium"
                    style={styles.primaryText}
                    useTranslationText={false}
                  />
                  <CustomText
                    text={`${availableRubric.sections.length} categories - ${availableRubric.sourceFile.fileName}`}
                    size="xs"
                    style={styles.secondaryText}
                    useTranslationText={false}
                  />
                </View>
                <CustomButton
                  text={activatingRubricId === availableRubric.id ? "Activating..." : "Activate"}
                  size="s"
                  styleType="lightStroke"
                  disabled={activatingRubricId != null}
                  useTranslationText={false}
                  onPress={() => onActivateRubric(availableRubric.id)}
                />
              </View>
            ))}
          </View>
        ) : (
          <CustomText
            text="No inactive ready rubrics yet"
            size="xs"
            style={styles.secondaryText}
            useTranslationText={false}
          />
        )}
      </View>
    </View>
  );
}

export function RubricReadyFooter({ rubric, savingRubric, onUpdateRubric, onReplaceRubric }: RubricReadyFooterProps) {
  const hasAnyEdits = rubric.sections.some(
    (section) => section.isEdited || section.criteria.some((criterion) => criterion.isEdited || criterion.isDeleted)
  );

  const handleResetToOriginal = () => {
    onUpdateRubric({
      sections: rubric.sections
        .filter((section) => section.isEdited)
        .map((section) => ({ id: section.id, resetToOriginal: true })),
      criteria: rubric.sections.flatMap((section) =>
        section.criteria
          .filter((criterion) => criterion.isEdited || criterion.isDeleted)
          .map((criterion) => ({ id: criterion.id, resetToOriginal: true }))
      ),
    });
  };

  return (
    <View style={styles.footerActions}>
      {hasAnyEdits ? (
        <CustomButton
          text={savingRubric ? "Resetting..." : "Reset to Original"}
          styleType="lightStroke"
          size="m"
          disabled={savingRubric}
          useTranslationText={false}
          onPress={handleResetToOriginal}
          leftIcon={<ArrowCounterClockwiseIcon color="#344054" size={16} weight="bold" />}
        />
      ) : null}
      <CustomButton
        text="Replace Rubric"
        styleType="lightStroke"
        weight="medium"
        style={{ justifyContent: "center", color: "#344054" }}
        size="s"
        useTranslationText={false}
        onPress={onReplaceRubric}
        leftIcon={<UploadSimpleIcon color="#344054" size={16} weight="bold" />}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  contentInner: {
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 20,
  },
  primaryText: {
    color: "#101828",
  },
  secondaryText: {
    color: "#344054",
  },
  warningText: {
    color: "#B54708",
  },
  calloutText: {
    flex: 1,
    gap: 4,
  },
  warningCallout: {
    marginTop: 16,
    flexDirection: "row",
    gap: 10,
    padding: 12,
    borderRadius: 8,
    backgroundColor: "#FFFAEB",
    borderWidth: 1,
    borderColor: "#FEDF89",
  },
  activeRubricCard: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: "#D1E0FF",
    backgroundColor: "#F5F8FF",
    borderRadius: 8,
  },
  activeRubricTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  activeRubricText: {
    flex: 1,
    minWidth: 0,
  },
  readyBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: "#E3FBCC",
  },
  readyBadgeText: {
    color: "#2B5314",
  },
  editedBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: "#FEDF89",
  },
  editedBadgeText: {
    color: "#93370D",
  },
  sectionEyebrow: {
    color: "#344054",
  },
  sectionList: {
    gap: 0,
    borderTopWidth: 1,
    borderTopColor: "#F2F4F7",
  },
  sectionCard: {
    borderBottomWidth: 1,
    borderBottomColor: "#F2F4F7",
    backgroundColor: "#FFFFFF",
    overflow: "visible",
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "stretch",
    gap: 12,
    paddingHorizontal: 0,
    paddingVertical: 12,
  },
  sectionCaret: {
    alignItems: "center",
    justifyContent: "center",
    minHeight: 48,
  },
  sectionHeaderExpanded: {
    marginBottom: 8,
  },
  sectionHeaderText: {
    flex: 1,
    gap: 4,
    minWidth: 0,
  },
  sectionTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  sectionTitle: {
    color: "#101828",
    flex: 1,
  },
  sectionMetaText: {
    color: "#667085",
    flexShrink: 0,
  },
  sectionProgressTrack: {
    height: 6,
    borderRadius: 8,
    backgroundColor: "#F2F4F7",
    overflow: "hidden",
    marginTop: 4,
  },
  sectionProgressFill: {
    height: "100%",
    borderRadius: 8,
    backgroundColor: "#3E68FE",
  },
  sectionEditButton: {
    alignItems: "center",
    justifyContent: "center",
    minWidth: 40,
    minHeight: 40,
    borderRadius: 8,
  },
  sectionEditPanel: {
    gap: 8,
    paddingLeft: 28,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#D1E0FF",
  },
  inputLabel: {
    color: "#667085",
    textTransform: "uppercase",
  },
  sectionTitleInput: {
    minHeight: 36,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    borderRadius: 8,
    color: "#101828",
    fontSize: 14,
    fontWeight: "500",
  },
  sectionDescriptionInput: {
    minHeight: 76,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    borderRadius: 8,
    color: "#344054",
    fontSize: 12,
    lineHeight: 18,
    textAlignVertical: "top",
  },
  editActions: {
    flexDirection: "row",
    gap: 8,
  },
  criteriaList: {
    gap: 8,
    paddingLeft: 28,
    paddingRight: 0,
    paddingBottom: 12,
    overflow: "visible",
  },
  otherRubricsSection: {
    gap: 8,
    paddingTop: 16,
  },
  otherRubricsList: {
    gap: 8,
  },
  otherRubricCard: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
  },
  footerActions: {
    gap: 8,
    flexShrink: 0,
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
    backgroundColor: "#FFFFFF",
  },
});

export default RubricReady;
